// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var _a;
// Harvest answers a refused monitoring request with `{message, extensions:
// {code, ...}}`, which Hasura passes on as the GraphQL error's extensions (or,
// in some setups, keeps in `extensions.internal.response.body`).
/** The codes Harvest's monitoring routes put in a refusal's `extensions.code`; the view and the editor share them. */
export var EMonitoringErrorCode;
(function (EMonitoringErrorCode) {
    /** 409: someone saved first; `{current_revision, author, time}`. */
    EMonitoringErrorCode["CONFLICT"] = "MONITORING_CONFLICT";
    /** 503 with Retry-After: the event is busy; asked again after a moment. */
    EMonitoringErrorCode["BUSY"] = "MONITORING_BUSY";
    /** 403: a region, Post or country outside what the viewer may see. */
    EMonitoringErrorCode["FORBIDDEN_SCOPE"] = "MONITORING_FORBIDDEN_SCOPE";
    /** 410: the update being exported is no longer kept. */
    EMonitoringErrorCode["SNAPSHOT_PRUNED"] = "MONITORING_SNAPSHOT_PRUNED";
    /** 503: the chart engine is not answering. */
    EMonitoringErrorCode["CHECKS_UNAVAILABLE"] = "MONITORING_CHECKS_UNAVAILABLE";
    /** 403: the event is locked down. */
    EMonitoringErrorCode["LOCKED_DOWN"] = "MONITORING_LOCKED_DOWN";
    /** 404: the dashboard or widget is no longer configured. */
    EMonitoringErrorCode["NOT_FOUND"] = "MONITORING_NOT_FOUND";
    /** 400 or 422: the request is not one Harvest accepts, such as a bound without an offset. */
    EMonitoringErrorCode["BAD_REQUEST"] = "MONITORING_BAD_REQUEST";
    /** 422 with `problems`: values Harvest checked and refused, such as a widget's pick. */
    EMonitoringErrorCode["INVALID"] = "MONITORING_INVALID";
})(EMonitoringErrorCode || (EMonitoringErrorCode = {}));
/** How long a busy answer waits before it is asked again. */
export var BUSY_RETRY_MS = 3000;
var CODES = new Set(Object.values(EMonitoringErrorCode));
var MESSAGES = (_a = {},
    _a[EMonitoringErrorCode.CONFLICT] = "monitoring.errors.conflict",
    _a[EMonitoringErrorCode.BUSY] = "monitoring.errors.busy",
    _a[EMonitoringErrorCode.FORBIDDEN_SCOPE] = "monitoring.errors.forbiddenScope",
    _a[EMonitoringErrorCode.SNAPSHOT_PRUNED] = "monitoring.errors.snapshotPruned",
    _a[EMonitoringErrorCode.CHECKS_UNAVAILABLE] = "monitoring.errors.checksUnavailable",
    _a[EMonitoringErrorCode.LOCKED_DOWN] = "monitoring.errors.lockedDown",
    _a[EMonitoringErrorCode.NOT_FOUND] = "monitoring.errors.notFound",
    _a[EMonitoringErrorCode.BAD_REQUEST] = "monitoring.errors.badRequest",
    _a[EMonitoringErrorCode.INVALID] = "monitoring.errors.invalid",
    _a);
var record = function (value) {
    return value && typeof value === "object" ? value : {};
};
function parsed(body) {
    if (typeof body !== "string")
        return record(body);
    try {
        return record(JSON.parse(body));
    }
    catch (_a) {
        return {};
    }
}
/** Each error's extensions, and those of the handler's body when Hasura wraps it. */
function extensionsOf(error) {
    var graphQLErrors = record(error).graphQLErrors;
    if (!Array.isArray(graphQLErrors))
        return [];
    return graphQLErrors.flatMap(function (graphQLError) {
        var extensions = record(record(graphQLError).extensions);
        var body = parsed(record(record(extensions.internal).response).body);
        return [extensions, record(body.extensions)];
    });
}
/** The codes in an error, wherever Hasura put them. */
function codesOf(error) {
    return extensionsOf(error).map(function (extensions) { return extensions.code; });
}
/** The `problems` of a MONITORING_INVALID refusal. */
export function monitoringProblems(error) {
    return extensionsOf(error).flatMap(function (extensions) {
        return Array.isArray(extensions.problems)
            ? extensions.problems.flatMap(function (problem) {
                var _a = record(problem), code = _a.code, path = _a.path, message = _a.message;
                return typeof code === "string"
                    ? [
                        {
                            code: code,
                            path: typeof path === "string" ? path : "",
                            message: typeof message === "string" ? message : "",
                        },
                    ]
                    : [];
            })
            : [];
    });
}
/** Harvest's code for a failed monitoring request, when it is one the view knows. */
export function monitoringErrorCode(error) {
    return codesOf(error).find(function (code) { return typeof code === "string" && CODES.has(code); });
}
/** The translation key of what to tell the viewer about a failed request. */
export function monitoringErrorMessage(error) {
    var code = monitoringErrorCode(error);
    return code ? MESSAGES[code] : "monitoring.errors.unknown";
}
